import addEditBudgetItemDialogTemplate from './addEditBudgetItemDialog.template.html';

/*****************************************************************************
 * Service: addEditBudgetItemDialog */
/**
 * @ngdoc  service
 * @module budgets
 * @name   budgets.service:addEditBudgetItemDialog
 * @description
 * Utility to show a dialog for editing budget amounts for an account/item
 *****************************************************************************/
/* @ngInject */ export function addEditBudgetItemDialog($uibModal) {
  /***************************************************************************
   * Properties
   ***************************************************************************/
  /** Self reference. */ var mThis = this;

  /***************************************************************************
   * show */
  /**
   * @ngdoc     method
   * @module    budgets
   * @methodOf  budgets.service:addEditBudgetItemDialog
   * @param     {object}  account   Budget account to edit.
   * @param     {number}  fyMonth   Month for beginning of fiscal year
   * @description
   * Show the modal dialog for editing a budget.
   ***************************************************************************/
  mThis.show = function (account, fyMonth) {
    return $uibModal.open({
      animation: true,
      backdrop: 'static',
      controller: addEditBudgetController,
      resolve: {
        account: function () {
          return account;
        },
        fyMonth: function () {
          return fyMonth;
        }
      },
      size: 'lg',
      template: addEditBudgetItemDialogTemplate
    }).result;
  };
}

/*****************************************************************************
 * Controller: addEditBudgetController */
/**
 * @ngdoc   controller
 * @module  budgets
 * @name    budgets.controller:addEditBudgetController
 * @description
 * Displays the budget item in a modal. Has the total amount for the year with
 * a breakdown of each month. When an amout is entered in to the Annual Total
 * the amount will be broken out in to the 12 months. Likewise when an amount
 * for a particular month is updated, the Annual Total will be updtaed. The
 * first month in the display will be the fiscal year start month.
 *****************************************************************************/
/* @ngInject */ function addEditBudgetController($scope, account, fyMonth, Tools) {
  /*-------------------------------------------------------*/
  /* Set the account on the scope to be what was passed in */
  /*-------------------------------------------------------*/
  $scope.account = account;
  /*-------------------------------------------------------------*/
  /* Set the accounts total to the scope so that we are not      */
  /* directly updating the original total until the user clicks  */
  /* okay to confirm.                                            */
  /*-------------------------------------------------------------*/
  $scope.totalAmt = account.TotalAmt;

  /*---------------------------------------------------*/
  /* Array of months to be displayed. The first month  */
  /* in the table will be the fiscal year start month. */
  /*---------------------------------------------------*/
  var monthInputs = [
    {
      field: 'month1',
      amount: $scope.account.month1,
      displayName: 'January'
    },
    {
      field: 'month2',
      amount: $scope.account.month2,
      displayName: 'February'
    },
    { field: 'month3', amount: $scope.account.month3, displayName: 'March' },
    { field: 'month4', amount: $scope.account.month4, displayName: 'April' },
    { field: 'month5', amount: $scope.account.month5, displayName: 'May' },
    { field: 'month6', amount: $scope.account.month6, displayName: 'June' },
    { field: 'month7', amount: $scope.account.month7, displayName: 'July' },
    { field: 'month8', amount: $scope.account.month8, displayName: 'August' },
    {
      field: 'month9',
      amount: $scope.account.month9,
      displayName: 'September'
    },
    {
      field: 'month10',
      amount: $scope.account.month10,
      displayName: 'October'
    },
    {
      field: 'month11',
      amount: $scope.account.month11,
      displayName: 'November'
    },
    {
      field: 'month12',
      amount: $scope.account.month12,
      displayName: 'December'
    }
  ];

  /*-------------------------------------------------------*/
  /* Create the month array to be displayed. Since we      */
  /* want the first month diplayed to be the fiscal year   */
  /* start month, splice the end of the array based on     */
  /* this month then add back the other months to the end. */
  /*-------------------------------------------------------*/
  $scope.months = monthInputs
    .splice(fyMonth - 1, monthInputs.length - (fyMonth - 1))
    .concat(monthInputs);

  /******************************************************************************
  * cancel
  /**
  * Closes the dialog and refer that no changes/updates were made to the budget.
  *******************************************************************************/
  $scope.cancel = function () {
    $scope.$close({ updated: false });
  };

  /***************************************************************************
  * close
  /**
  * Closes the dialog and passes the new amounts back to what called the
  * service. 
  ***************************************************************************/
  $scope.close = function () {
    /*----------------------------------------------------------*/
    /* Go through each of the month and make sure the field     */
    /* is properly set with the correct amount value and also   */
    /* update the TotalAmt with the newly calculated amount.    */
    /*----------------------------------------------------------*/
    for (let i = 0; i < 12; i++) {
      const month = $scope.months[i];
      $scope.account[month.field] = month.amount;
    }
    $scope.account.TotalAmt = $scope.totalAmt;
    account.updated = true;
    $scope.$close(account);
  };

  /***************************************************************************
  * distributeAnnual
  /**
  * Distributes the annual budget amount evenly across all 12 months. The first
  * month will be different if the total amount is not evenly divisible by 12.
  * This is called on blur from the Annual Total field. 
  ***************************************************************************/
  $scope.distributeAnnual = function () {
    /*----------------------------------------------------------------------------*/
    /* This check prevents each of the months from recalculating everytime this   */
    /* function is called and only allows the recalculation to happen if the      */
    /* total has been updated and is different from its default.                  */
    /*----------------------------------------------------------------------------*/
    if (account.TotalAmt == $scope.totalAmt) return;

    var monthAmt = Tools.roundMoney($scope.totalAmt / 12);
    var monthSum = Tools.roundMoney(monthAmt * 12);
    var annualDiff = Tools.roundMoney($scope.totalAmt - monthSum);
    var month1Amt = Tools.roundMoney(monthAmt + annualDiff);

    /*--------------------------------------------------*/
    /* Set the amount for the first month in the array  */
    /* as this may be different from the other amounts. */
    /*--------------------------------------------------*/
    $scope.months[0].amount = Tools.roundMoney(month1Amt);

    /*---------------------------------------------*/
    /* Set the amounts for the rest of the months. */
    /*---------------------------------------------*/
    for (var i = 1; i < 12; i++) {
      $scope.months[i].amount = monthAmt;
      var monthNumber = $scope.months[i].field;
      $scope.account[monthNumber] = $scope.months[i].amount;
    }
  };

  /***************************************************************************
  * setAnnual
  /**
  * Calculates the Annual Total amount for the total of all months. This is 
  * called on blur from any of the month amount fields.  
  ***************************************************************************/
  $scope.setAnnual = function () {
    var total = 0;
    /*------------------------------------------------------------------------------------------------------*/
    /* Because this function is fired everytime any of the input field belonging to a month                 */
    /* is interacted, the month fields where a user has not entered in a number will come in                */
    /* here as undefined. And since we are getting the sum of the 12 input fields, if we have               */
    /* even one field as undefined it will set the sum as NaN.                                              */
    /* Ex: 12.00 + 11.00 + 1.00 + 1.00 + 1.00 + 1.00 + 1.00 + 1.00 + 1.00 + 1.00 + 1.00 + undefined = NaN   */
    /* To prevent this issue we need to make sure to format the $scope.months[i].amount                     */
    /* when we are summing, by doing so we can get this result --> Tools.coalesce(undefined, 0) === 0.0     */
    /*------------------------------------------------------------------------------------------------------*/
    for (var i = 0; i < 12; i++) {
      total += Tools.coalesce($scope.months[i].amount, 0);
    }
    $scope.totalAmt = Tools.roundMoney(total);
  };
}
